import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { useToast } from "@/components/feedback/toaster";
import { fetchIacExport, type IacPlanSummary, useIacApply, useIacPlan } from "@/api/iac";

export const Route = createFileRoute("/_app/settings/iac")({
  component: IacSettings,
});

const KINDS = ["Pipeline", "Connector", "Schedule"] as const;

function IacSettings() {
  const toast = useToast();
  const plan = useIacPlan();
  const apply = useIacApply();

  const [kinds, setKinds] = useState<string[]>([...KINDS]);
  const [exported, setExported] = useState("");
  const [source, setSource] = useState("");
  const [prune, setPrune] = useState(false);
  const [planned, setPlanned] = useState<IacPlanSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runExport() {
    setError(null);
    try {
      const yaml = await fetchIacExport(kinds);
      setExported(yaml);
      if (!source.trim()) setSource(yaml);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    }
  }

  async function runPlan() {
    setError(null);
    setPlanned(null);
    try {
      setPlanned(await plan.mutateAsync(source));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Plan failed");
    }
  }

  async function runApply() {
    setError(null);
    try {
      const res = await apply.mutateAsync({ source, prune });
      setPlanned(res.planned);
      toast.success("Applied");
      await runExport();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Apply failed");
    }
  }

  function download() {
    const blob = new Blob([exported], { type: "application/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "udip.yaml";
    a.click();
    URL.revokeObjectURL(url);
  }

  const changeCount =
    (planned?.create.length ?? 0) +
    (planned?.update.length ?? 0) +
    (prune ? (planned?.delete.length ?? 0) : 0);

  return (
    <PageContainer maxWidth={860}>
      <PageHeader
        title="Everything as code"
        description="Export pipelines, connectors and schedules as declarative YAML, then plan and apply changes like infrastructure."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Export
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
            {KINDS.map((k) => (
              <Chip
                key={k}
                label={k}
                size="small"
                color={kinds.includes(k) ? "primary" : "default"}
                variant={kinds.includes(k) ? "filled" : "outlined"}
                onClick={() =>
                  setKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]))
                }
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={runExport} disabled={kinds.length === 0}>
              Export
            </Button>
            <Button onClick={download} disabled={!exported}>
              Download udip.yaml
            </Button>
          </Stack>
          {exported ? (
            <Box
              component="pre"
              sx={{
                mt: 2,
                p: 1.5,
                maxHeight: 260,
                overflow: "auto",
                bgcolor: "background.default",
                borderRadius: 1,
                fontSize: 12,
              }}
            >
              {exported}
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Plan &amp; apply
          </Typography>
          <TextField
            multiline
            minRows={8}
            fullWidth
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="Paste YAML or JSON resources here (or click Export to prefill)…"
            slotProps={{ htmlInput: { style: { fontFamily: "monospace", fontSize: 12 } } }}
          />
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ mt: 1.5, alignItems: "center", flexWrap: "wrap" }}
          >
            <Button
              variant="outlined"
              onClick={runPlan}
              disabled={!source.trim() || plan.isPending}
            >
              {plan.isPending ? "Planning…" : "Plan"}
            </Button>
            <Button
              variant="contained"
              color={prune ? "error" : "primary"}
              onClick={runApply}
              disabled={!planned || changeCount === 0 || apply.isPending}
            >
              {apply.isPending ? "Applying…" : `Apply${changeCount ? ` (${changeCount})` : ""}`}
            </Button>
            <FormControlLabel
              control={
                <Switch size="small" checked={prune} onChange={(e) => setPrune(e.target.checked)} />
              }
              label="Prune (delete resources absent from the source)"
            />
          </Stack>

          {planned ? (
            <Box sx={{ mt: 2 }}>
              <PlanList label="Create" items={planned.create} color="success.main" />
              <PlanList
                label="Update"
                items={planned.update.map((u) => `${u.resource}  ·  ${u.changes.join(", ")}`)}
                color="warning.main"
              />
              <PlanList label="Delete" items={planned.delete} color="error.main" muted={!prune} />
              <Typography variant="caption" color="text.secondary">
                {planned.unchanged} unchanged
                {!prune && planned.delete.length > 0
                  ? ` · ${planned.delete.length} not in source (enable Prune to remove)`
                  : ""}
              </Typography>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function PlanList({
  label,
  items,
  color,
  muted,
}: {
  label: string;
  items: string[];
  color: string;
  muted?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <Box sx={{ mb: 1, opacity: muted ? 0.5 : 1 }}>
      <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
        {label} ({items.length})
      </Typography>
      <Box component="ul" sx={{ m: "2px 0 0", pl: 2.5 }}>
        {items.map((it, i) => (
          <Typography key={i} component="li" variant="body2" sx={{ fontFamily: "monospace" }}>
            {it}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
